export type DepositPlaidAccount = {
  account_id: string;
  account_type?: string | null;
  name?: string | null;
  mask?: string | null;
  // Current Plaid account id when the snapshot account_id is firm-data's UUID.
  // Absent on legacy Plaid snapshots, where account_id is already the Plaid id.
  plaid_account_id?: string | null;
};

export type DepositBankRelationship = {
  plaid_id?: string | null;
  state?: string | null;
  masked_number?: string | null;
};

const DEPOSIT_ELIGIBLE_ACCOUNT_TYPES = new Set([
  'depository/checking',
  'depository/savings',
  'checking',
  'savings',
]);

export const isDepositEligiblePlaidAccount = (account: DepositPlaidAccount): boolean => {
  const accountType = String(account.account_type || '').toLowerCase();
  return DEPOSIT_ELIGIBLE_ACCOUNT_TYPES.has(accountType);
};

export const getDepositEligiblePlaidAccounts = <TAccount extends DepositPlaidAccount>(
  accounts: readonly TAccount[],
): TAccount[] => accounts.filter(isDepositEligiblePlaidAccount);

export const getAccountFundingPlaidId = (account: DepositPlaidAccount): string => {
  const processorAccountId = account.plaid_account_id?.trim();
  return processorAccountId || account.account_id;
};

export const accountMatchesFundingPlaidId = (
  account: DepositPlaidAccount,
  plaidId: string,
): boolean => account.account_id === plaidId || account.plaid_account_id === plaidId;

export const isApprovedDepositBankRelationship = (
  relationship?: DepositBankRelationship,
): boolean => relationship?.state === 'APPROVED';

const relationshipPlaidId = (relationship: DepositBankRelationship): string | undefined =>
  relationship.plaid_id?.trim() || undefined;

export const findDepositSourceAccount = <TAccount extends DepositPlaidAccount>(
  accounts: readonly TAccount[],
  plaidId: string | undefined,
): TAccount | undefined => {
  if (!plaidId) {
    return undefined;
  }

  return getDepositEligiblePlaidAccounts(accounts).find((account) =>
    accountMatchesFundingPlaidId(account, plaidId),
  );
};

export const getApprovedDepositPlaidAccount = <TAccount extends DepositPlaidAccount>(
  accounts: readonly TAccount[],
  bankRelationships: readonly DepositBankRelationship[],
): TAccount | undefined => {
  const approvedPlaidIds = new Set(
    bankRelationships
      .filter(isApprovedDepositBankRelationship)
      .map(relationshipPlaidId)
      .filter((plaidId): plaidId is string => Boolean(plaidId)),
  );

  return getDepositEligiblePlaidAccounts(accounts).find(
    (account) =>
      approvedPlaidIds.has(account.account_id) ||
      approvedPlaidIds.has(getAccountFundingPlaidId(account)),
  );
};

export const getPreferredDepositPlaidAccount = <TAccount extends DepositPlaidAccount>(
  accounts: readonly TAccount[],
  bankRelationships: readonly DepositBankRelationship[] = [],
): TAccount | undefined =>
  getApprovedDepositPlaidAccount(accounts, bankRelationships) ??
  getDepositEligiblePlaidAccounts(accounts)[0];

// The id Apex /cash/deposits expects as plaid_id. For a legacy Plaid snapshot
// that is account_id. For firm-data, snapshot account_id is a UUID, so prefer
// an Apex bank-relationship plaid_id once those have loaded.
export const getPreferredDepositFundingPlaidId = (
  accounts: readonly DepositPlaidAccount[],
  bankRelationships: readonly DepositBankRelationship[] = [],
): string | undefined => {
  const eligibleAccounts = getDepositEligiblePlaidAccounts(accounts);
  if (eligibleAccounts.length === 0) {
    return undefined;
  }

  const eligibleFundingIds = new Set(eligibleAccounts.map(getAccountFundingPlaidId));
  const approvedIds = bankRelationships
    .filter(isApprovedDepositBankRelationship)
    .map(relationshipPlaidId)
    .filter((plaidId): plaidId is string => Boolean(plaidId));

  const matchedApproved = approvedIds.find((plaidId) => eligibleFundingIds.has(plaidId));
  if (matchedApproved) {
    return matchedApproved;
  }

  // Firm-data checking/savings do not share an id with Apex's plaid_id.
  // Processor-token sync only creates relationships for depository accounts,
  // so the first approved relationship is still a valid funding source.
  if (approvedIds[0]) {
    return approvedIds[0];
  }

  const pendingId = bankRelationships
    .filter((relationship) => relationship.state === 'PENDING')
    .map(relationshipPlaidId)
    .find((plaidId): plaidId is string => Boolean(plaidId));
  if (pendingId) {
    return pendingId;
  }

  return getAccountFundingPlaidId(eligibleAccounts[0]);
};

export const formatDepositSourceLabel = (
  relationship: DepositBankRelationship,
  sourceAccount?: DepositPlaidAccount,
): string => {
  const mask = sourceAccount?.mask?.trim() || relationship.masked_number?.trim() || '';
  const accountName = sourceAccount?.name?.trim();
  let name = accountName
    ? mask
      ? `${accountName} (**** ${mask})`
      : accountName
    : mask
      ? `Bank **** ${mask}`
      : 'Linked bank';

  if (relationship.state && relationship.state !== 'APPROVED') {
    name += ` ${relationship.state}`;
  }

  return name;
};
